type NavigableApp = {
  navigate: (path: string) => void;
};

const PAGE_QUERY_SUFFIX = "Page";
const PAGE_SIZE_QUERY_SUFFIX = "PageSize";

export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export type TablePageSize = (typeof TABLE_PAGE_SIZE_OPTIONS)[number];

export type TablePaginationState = {
  page: number;
  pageSize: TablePageSize;
};

type CreateUrlSyncedTablePaginationControllerParams = {
  app: NavigableApp;
  prefix: string;
  totalItems: number;
  persistedPagination?: Partial<TablePaginationState>;
  onPatchPagination: (pagination: TablePaginationState) => void;
};

export type UrlSyncedTablePaginationController = {
  pagination: TablePaginationState;
  totalPages: number;
  firstVisibleIndex: number;
  lastVisibleIndex: number;
  setPageSize: (size: TablePageSize) => void;
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  goToLastPage: () => void;
  resetToFirstPage: () => void;
  paginateItems: <TItem>(items: TItem[]) => TItem[];
};

const DEFAULT_TABLE_PAGINATION: TablePaginationState = {
  page: 1,
  pageSize: 25,
};

const toPositiveInteger = (
  value: string | null | undefined,
  fallback: number
) => {
  if (!value) {
    return fallback;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return parsed;
};

const toAllowedPageSize = (
  value: string | null | undefined,
  fallback: TablePageSize
): TablePageSize => {
  const parsed = Number.parseInt(value ?? "", 10);

  if (TABLE_PAGE_SIZE_OPTIONS.includes(parsed as TablePageSize)) {
    return parsed as TablePageSize;
  }

  return fallback;
};

const buildPageKey = (prefix: string) => `${prefix}${PAGE_QUERY_SUFFIX}`;

const buildPageSizeKey = (prefix: string) =>
  `${prefix}${PAGE_SIZE_QUERY_SUFFIX}`;

export const readTablePaginationFromUrl = (
  prefix: string,
  fallback?: Partial<TablePaginationState>
): TablePaginationState => {
  const defaults: TablePaginationState = {
    page: fallback?.page ?? DEFAULT_TABLE_PAGINATION.page,
    pageSize: fallback?.pageSize ?? DEFAULT_TABLE_PAGINATION.pageSize,
  };

  const params = new URLSearchParams(window.location.search);

  const nextPage = toPositiveInteger(
    params.get(buildPageKey(prefix)),
    defaults.page
  );
  const nextPageSize = toAllowedPageSize(
    params.get(buildPageSizeKey(prefix)),
    defaults.pageSize
  );

  return {
    page: nextPage,
    pageSize: nextPageSize,
  };
};

export const writeTablePaginationToUrl = (
  app: NavigableApp,
  prefix: string,
  pagination: TablePaginationState
) => {
  const params = new URLSearchParams(window.location.search);

  params.set(buildPageKey(prefix), String(pagination.page));
  params.set(buildPageSizeKey(prefix), String(pagination.pageSize));

  const query = params.toString();
  const nextPath = `${window.location.pathname}${query ? `?${query}` : ""}${
    window.location.hash
  }`;

  app.navigate(nextPath);
};

export const clampTablePagination = (
  pagination: TablePaginationState,
  totalItems: number
): TablePaginationState => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pagination.pageSize));

  return {
    page: Math.min(Math.max(1, pagination.page), totalPages),
    pageSize: pagination.pageSize,
  };
};

export const createUrlSyncedTablePaginationController = ({
  app,
  prefix,
  totalItems,
  persistedPagination,
  onPatchPagination,
}: CreateUrlSyncedTablePaginationControllerParams): UrlSyncedTablePaginationController => {
  const urlPagination = readTablePaginationFromUrl(prefix, persistedPagination);

  if (
    persistedPagination?.page !== urlPagination.page ||
    persistedPagination?.pageSize !== urlPagination.pageSize
  ) {
    onPatchPagination(urlPagination);
  }

  const clampedPagination = clampTablePagination(urlPagination, totalItems);

  const totalPages = Math.max(
    1,
    Math.ceil(totalItems / clampedPagination.pageSize)
  );
  const firstVisibleIndex =
    totalItems === 0
      ? 0
      : (clampedPagination.page - 1) * clampedPagination.pageSize + 1;
  const lastVisibleIndex = Math.min(
    clampedPagination.page * clampedPagination.pageSize,
    totalItems
  );

  const syncPagination = (next: TablePaginationState) => {
    writeTablePaginationToUrl(app, prefix, next);
    onPatchPagination(next);
  };

  return {
    pagination: clampedPagination,
    totalPages,
    firstVisibleIndex,
    lastVisibleIndex,
    setPageSize: (size) => {
      syncPagination({
        page: 1,
        pageSize: size,
      });
    },
    goToFirstPage: () => {
      if (clampedPagination.page <= 1) {
        return;
      }

      syncPagination({
        ...clampedPagination,
        page: 1,
      });
    },
    goToPreviousPage: () => {
      if (clampedPagination.page <= 1) {
        return;
      }

      syncPagination({
        ...clampedPagination,
        page: clampedPagination.page - 1,
      });
    },
    goToNextPage: () => {
      if (clampedPagination.page >= totalPages) {
        return;
      }

      syncPagination({
        ...clampedPagination,
        page: clampedPagination.page + 1,
      });
    },
    goToLastPage: () => {
      if (clampedPagination.page >= totalPages) {
        return;
      }

      syncPagination({
        ...clampedPagination,
        page: totalPages,
      });
    },
    resetToFirstPage: () => {
      syncPagination({
        ...clampedPagination,
        page: 1,
      });
    },
    paginateItems: <TItem>(items: TItem[]) =>
      items.slice(
        (clampedPagination.page - 1) * clampedPagination.pageSize,
        clampedPagination.page * clampedPagination.pageSize
      ),
  };
};
